// 運行指示書（リデザイン）の列ごとの表示メタ。
// route-planner リファレンス OPERATION_FIELD_DEFS を本番の列 title に対応づけたもの。
//   - width:     colgroup 用の相対幅（合計で正規化して使う）
//   - important: 重要列（太字＋ヘッダ背景＋優先幅）
//   - groupBy:   同一地点ブロック内で重複値を 〃（compact）/ rowSpan（detail）圧縮
//   - numeric:   右寄せ・nowrap（数値列）
//   - wrap:      詳細モードで折返し許可
export interface OperationColumnMeta {
  width: number;
  important: boolean;
  groupBy: boolean;
  numeric: boolean;
  wrap: boolean;
}

type MetaSeed = {
  width: number;
  important?: boolean;
  groupBy?: boolean;
  numeric?: boolean;
  wrap?: boolean;
};

// 本番の列 title をキーにしたメタ定義。
// 本番とリファレンスで名称が違う列（合計重量/重量, 合計体積/体積, 荷主/荷主名 等）は
// 両方のキーを登録してエイリアス対応する。
const COLUMN_META_SEED: Record<string, MetaSeed> = {
  到着予定時刻: { width: 6, groupBy: true, important: true },
  荷主: { width: 7, groupBy: true },
  荷主名: { width: 7, groupBy: true },
  担当者: { width: 6, groupBy: true },
  荷主担当者: { width: 6, groupBy: true },
  電話番号: { width: 7, groupBy: true },
  荷主電話番号: { width: 7, groupBy: true },
  メールアドレス: { width: 8, groupBy: true, wrap: true },
  作業: { width: 3, groupBy: true },
  作業場所: { width: 8, groupBy: true },
  作業場所住所: { width: 14, wrap: true, groupBy: true, important: true },
  // 伝票数は行ごとに 1 を加算し同一明細のみ合算される「明細単位の値」のため groupBy しない
  // （地点単位で rowSpan 結合すると2行目以降の件数が隠れて誤帳票になる）。
  伝票数: { width: 4, numeric: true },
  伝票No: { width: 7 },
  注文コード: { width: 7 },
  積日: { width: 4, groupBy: true },
  積地: { width: 8, groupBy: true },
  積地住所: { width: 14, wrap: true, groupBy: true, important: true },
  積地時間帯: { width: 6, groupBy: true },
  降日: { width: 4, groupBy: true },
  降地: { width: 10, groupBy: true },
  降地住所: { width: 18, wrap: true, groupBy: true, important: true },
  降地時間帯: { width: 6, groupBy: true },
  品名: { width: 11 },
  荷姿: { width: 4 },
  荷扱い: { width: 5 },
  数量: { width: 3, numeric: true },
  重量: { width: 4, numeric: true },
  合計重量: { width: 4, numeric: true },
  体積: { width: 4, numeric: true },
  合計体積: { width: 4, numeric: true },
  備考: { width: 12, wrap: true },
  運転手名: { width: 6, groupBy: true },
  車両番号: { width: 5, groupBy: true },
  車両タイプ: { width: 5, groupBy: true },
  指定車種: { width: 5, groupBy: true },
  荷台高: { width: 4, groupBy: true },
  荷台幅: { width: 4, groupBy: true },
  荷台長: { width: 4, groupBy: true },
  床仕様: { width: 5, groupBy: true },
  '指定装置・特徴': { width: 8, groupBy: true },
  付帯作業備考: { width: 10, wrap: true },
};

const DEFAULT_META: OperationColumnMeta = {
  width: 6, important: false, groupBy: false, numeric: false, wrap: false,
};

export function getOperationColumnMeta(title: string): OperationColumnMeta {
  const seed = COLUMN_META_SEED[title];
  if (!seed) {
    // 積地/降地時間枠（開始①〜③・終了①〜③）は地点単位なので groupBy（merge/〃）扱いにする。
    if (title.includes('時間枠')) {
      return { ...DEFAULT_META, groupBy: true };
    }
    return { ...DEFAULT_META };
  }
  return {
    width: seed.width,
    important: seed.important ?? false,
    groupBy: seed.groupBy ?? false,
    numeric: seed.numeric ?? false,
    wrap: seed.wrap ?? false,
  };
}
